import { useState, useEffect } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';

/**
 * Custom hook for simple search functionality using RPC functions
 * Designed for dropdowns that need to search and return simple lists
 * 
 * @param rpcFunction Name of the RPC function to call
 * @param searchTerm Current search term
 * @param minSearchLength Minimum characters required to trigger search
 * @param limitResults Maximum number of results to return
 * @returns Object with data and loading state
 */
export const useSimpleSearch = <T>(
  rpcFunction: string,
  searchTerm: string,
  minSearchLength: number = 3,
  limitResults: number = 50
) => {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const supabase = useSupabaseClient();

  useEffect(() => {
    const performSearch = async () => {
      if (searchTerm.length >= minSearchLength) {
        console.log(`🔍 useSimpleSearch: Searching "${searchTerm}" with ${rpcFunction}`);
        setLoading(true);
        try {
          const { data: results, error } = await supabase
            .rpc(rpcFunction, {
              search_term: searchTerm,
              limit_results: limitResults
            });

          if (error) {
            console.error(`❌ Error in ${rpcFunction}:`, error);
            setData([]);
          } else {
            console.log(`✅ ${rpcFunction} returned:`, results);
            setData(results || []);
          }
        } catch (error) {
          console.error(`💥 Exception in ${rpcFunction}:`, error);
          setData([]);
        } finally {
          setLoading(false);
        }
      } else {
        console.log(`⏳ Search term "${searchTerm}" too short (need ${minSearchLength})`);
        setData([]);
        setLoading(false);
      }
    };

    performSearch();
  }, [searchTerm, rpcFunction, minSearchLength, limitResults, supabase]);

  return { data, loading };
}; 